<template>
  <Teleport to="body">
    <div>
      <TransitionRoot
        :show="show"
        as="template"
      >
        <Dialog
          :open="show"
          class="relative z-50"
        >
          <TransitionChild
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-black/30" />
          </TransitionChild>

          <TransitionChild
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div class="fixed inset-0 overflow-y-auto">
              <!-- Container to center the panel -->
              <div class="flex min-h-full items-center justify-center ">
                <DialogPanel
                  class="relative transform overflow-hidden rounded-md bg-white text-left shadow-2xl transition-all w-full flex flex-col h-full"
                  :class="[widthClass]"
                >
                  <div
                    v-if="!hideHeader"
                    class="text-left flex-none bg-stone-100"
                  >
                    <div class="flex items-center">
                      <DialogTitle
                        as="h3"
                        class="text-lg flex-1 font-medium leading-6 text-gray-900 pl-6 py-3"
                      >
                        <slot name="title">
                          <span v-if="Array.isArray(title)">
                            {{ title[0] }} &mdash; {{ title[1] }}
                          </span>
                          <span v-else>
                            {{ title }}
                          </span>
                        </slot>
                      </DialogTitle>

                      <button
                        type="button"
                        tabindex="1000000"
                        class="flex-none rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mr-4"
                        @click="hide"
                      >
                        <span class="sr-only">Close</span>
                        <stormy-icon
                          name="x"
                          class="h-6 w-6"
                        />
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="hasHeader"
                    class="text-left flex-none bg-stone-100 my-0"
                  >
                    <slot name="header" />
                  </div>
                  <div
                    v-auto-animate
                    class="flex-1 flex mb-0  bg-white"
                    scrollbar="~ track-color-gray-50 thumb-color-blue-400 w-2 rounded pl-1"
                    :class="[heightClass, hasHeader ? '-mt-6' : '', overflow ? 'overflow-y-auto' : 'overflow-hidden flex flex-1']"
                  >
                    <div
                      :class="[height !== 'auto' ? 'px-4 flex flex-1 overflow-y-hidden' : 'px-0 py-1', overflow ? '' : '!p-0']"
                      class="w-full"
                    >
                      <slot name="content" />
                    </div>
                  </div>
                  <div class="bg-stone-50 py-3 flex flex-row-reverse px-4 flex-none w-full items-start">
                    <slot
                      name="footer"
                      :close="hide"
                    >
                      <button
                        type="button"
                        class="h-2 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        @click="hide"
                      >
                        Schließen
                      </button>
                    </slot>
                  </div>
                </DialogPanel>
              </div>
            </div>
          </TransitionChild>
        </Dialog>
      </TransitionRoot>
    </div>
  </Teleport>
</template>

<script setup>
import { useProp } from '@/composables/useProp.js'
import { computed, useSlots } from 'vue'

import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle
} from '@headlessui/vue'

const emit = defineEmits(['hide'])

const hide = () => {
  emit('hide')
}

const $slots = useSlots()
const hasHeader = computed(() => !!$slots.header)

const widthClass = computed(() => {
  return {
    xs: 'max-w-md',
    sm: 'max-w-xl',
    md: 'max-w-3xl ',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl'
  }[props.width]
})

const title = computed(() => {
  if (props.title.includes('|')) {
    return props.title.split('|').map(item => item.trim())
  }
  return props.title
})

const heightClass = computed(() => {
  return {
    auto: 'h-auto max-h-auto',
    sm: 'h-[25vh] max-h-[25vh] min-h-[25vh]',
    md: 'h-[50vh] max-h-[50vh] min-h-[50vh]',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl'
  }[props.height]
})

const props = defineProps({
  show: useProp(Boolean),
  title: useProp(String),
  canClose: useProp(Boolean, true),
  hideHeader: useProp(Boolean, false),
  overflow: useProp(Boolean, true),
  width: useProp(String, 'md'),
  height: useProp(String, 'md')
})
</script>
